/**
 * Session Security Manager
 * Handles automatic logout at midnight and cache cleanup
 */

import { clearAllCache } from './offlineDB';

// Clear all caches (IndexedDB + Service Worker)
export const clearAllCaches = async () => {
  try {
    // Clear IndexedDB cache
    await clearAllCache();
    
    // Clear Service Worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          if (cacheName.startsWith('ecoaudit-')) {
            return caches.delete(cacheName);
          }
        })
      );
    }
    
    // Clear localStorage (except essential items)
    const essentialKeys = ['theme']; // Keep theme preference
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!essentialKeys.includes(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    console.log('All caches cleared');
    return true;
  } catch (error) {
    console.error('Failed to clear caches:', error);
    return false;
  }
};

// Get today's date as YYYY-MM-DD string
const getTodayDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Calculate milliseconds until midnight
const getMsUntilMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0); // Next midnight
  return midnight.getTime() - now.getTime();
};

// Format time for display
const formatTime = (ms) => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours} saat ${minutes} dakika`;
};

let midnightTimer = null;
let warningTimer = null;

/**
 * Check if session should be expired (new day since last activity)
 * This is called when the app loads to handle the case where
 * user closed the browser and opened it the next day
 */
export const checkSessionExpiry = async () => {
  const lastActiveDate = localStorage.getItem('ecoaudit_last_active_date');
  const todayDate = getTodayDate();
  
  // Update last active date
  localStorage.setItem('ecoaudit_last_active_date', todayDate);
  
  // If we have a previous date and it's different from today, session expired
  if (lastActiveDate && lastActiveDate !== todayDate) {
    console.log(`Session expired - last active: ${lastActiveDate}, today: ${todayDate}`);
    await clearAllCaches();
    return true; // Session expired
  }
  
  return false; // Session still valid
};

/**
 * Update last active timestamp
 * Call this periodically while user is active
 */
export const updateLastActive = () => {
  localStorage.setItem('ecoaudit_last_active_date', getTodayDate());
};

/**
 * Setup automatic midnight logout
 * @param {Function} onLogout - Callback to execute on logout
 * @param {Function} onWarning - Callback to show warning before logout
 */
export const setupMidnightLogout = (onLogout, onWarning) => {
  // Clear any existing timers
  if (midnightTimer) clearTimeout(midnightTimer);
  if (warningTimer) clearTimeout(warningTimer);
  
  // Update last active date
  updateLastActive();
  
  const msUntilMidnight = getMsUntilMidnight();
  const warningTime = 5 * 60 * 1000; // 5 minutes before midnight
  
  console.log(`Automatic logout scheduled in ${formatTime(msUntilMidnight)}`);
  
  // Set warning timer (5 minutes before midnight)
  if (msUntilMidnight > warningTime) {
    warningTimer = setTimeout(() => {
      if (onWarning) {
        onWarning('Güvenlik nedeniyle 5 dakika içinde otomatik çıkış yapılacak');
      }
    }, msUntilMidnight - warningTime);
  }
  
  // Set midnight logout timer
  midnightTimer = setTimeout(async () => {
    console.log('Midnight reached - performing automatic logout');
    
    // Clear all caches
    await clearAllCaches();
    
    // Execute logout callback
    if (onLogout) {
      onLogout();
    }
    
    // Redirect to home
    window.location.href = '/';
  }, msUntilMidnight);
  
  // Also update last active date periodically (every 5 minutes)
  const activityInterval = setInterval(updateLastActive, 5 * 60 * 1000);
  
  return () => {
    if (midnightTimer) clearTimeout(midnightTimer);
    if (warningTimer) clearTimeout(warningTimer);
    clearInterval(activityInterval);
  };
};

/**
 * Get time remaining until auto-logout
 */
export const getTimeUntilLogout = () => {
  const ms = getMsUntilMidnight();
  return {
    ms,
    formatted: formatTime(ms),
    hours: Math.floor(ms / (1000 * 60 * 60)),
    minutes: Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
  };
};

/**
 * Perform manual logout with cache cleanup
 */
export const secureLogout = async (logoutCallback) => {
  // Clear all caches first
  await clearAllCaches();
  
  // Clear last active date
  localStorage.removeItem('ecoaudit_last_active_date');
  
  // Clear timers
  if (midnightTimer) clearTimeout(midnightTimer);
  if (warningTimer) clearTimeout(warningTimer);
  
  // Execute logout callback
  if (logoutCallback) {
    await logoutCallback();
  }
  
  return true;
};
